import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AmountInput } from '../../../core/shared/directives/amount-input.directive';
import { filter, finalize, Subscription } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
  DropdownOption,
  PKL_GRP,
} from '../../../core/shared/models/picklist.model';
import { PickListService } from '../../../core/shared/services/picklist.service';
import {
  CraditService,
  CreditTermParameterDto,
  CreditTermParameterGuarantorDto,
  SaveCreditTermParametersPayload,
} from '../shared/services/cradit.service';
import { Popup } from '../../../core/shared/components/popup/popup';
import { ToastService } from '../../../core/shared/services/toast.service';
import { blockManualDateEntry as blockManualDateEntryUtil } from '../../../core/shared/dynamic-form/date-input.utils';

export type BinaryValue = 0 | 1;

const KIBOR_DAY_MIN = 1;
const KIBOR_DAY_MAX = 31;

/** FACILITY_TYPE ref code hidden from the Add Repayment Term dropdown. */
const HIDDEN_TERM_TYPE = 30;

interface GuarantorFormControls {
  crdtGrntrSeq: FormControl<number | null>;
  guarantorRefCdSeq: FormControl<number>;
  percentage: FormControl<number | null>;
}

interface RepaymentTermFormControls {
  disbursementAmount: FormControl<number | null>;
  disbursementStartDate: FormControl<string | null>;
  disbursementEndDate: FormControl<string | null>;

  kiborVariable: FormControl<BinaryValue>;
  kiborDay: FormControl<number | null>;
  kiborResetPeriod: FormControl<number | null>;
  kiborSpreadRate: FormControl<number | null>;

  guarantorFlag: FormControl<BinaryValue>;
  guarantors: FormArray<FormGroup<GuarantorFormControls>>;

  isRenewable: FormControl<BinaryValue>;
  renewableAmount: FormControl<number | null>;

  isPrime: FormControl<BinaryValue>;
  primeAmount: FormControl<number | null>;
}

interface GlobalDetailsFormControls {
  repaymentStartDate: FormControl<string | null>;
  repaymentEndDate: FormControl<string | null>;
  installmentFrequency: FormControl<number | null>;
  creditEnsuranceFee: FormControl<number | null>;
  gracePeriod: FormControl<number | null>;
  numberOfInstallments: FormControl<number | null>;
  proposedExposure: FormControl<number | null>;
  inArrears: FormControl<BinaryValue>;
  upfrontCharges: FormControl<BinaryValue>;
  upfrontChargesPercentage: FormControl<number | null>;
}

interface RepaymentTermMeta {
  termRefCdSeq: number;
  crdtTrmParamsSeq: number | null;
  guarantorSelectionControl: FormControl<number[]>;
  subscriptions: Subscription;
  group: FormGroup<RepaymentTermFormControls>;
  removedGuarantors: CreditTermParameterGuarantorDto[];
}

export interface TermsParametersConfig {
  crdtTrmParamsLst?: Record<string, any>[];
  rpStrtDt?: string | null;
  rpEndDt?: string | null;
  rpymntFrqncy?: number | null;
  sprdRt?: number | null;
  crdtEnsrFee?: number | null;
  grcPrd?: number | null;
  noOfInstlmnt?: number | null;
  prpsdExpsr?: number | null;
  arrearFlg?: BinaryValue;
  upfrntFlg?: BinaryValue;
  upFrntFee?: number | null;
}

@Component({
  selector: 'app-terms-parameters',
  imports: [CommonModule, ReactiveFormsModule, Popup, TranslatePipe, AmountInput],
  templateUrl: './terms-parameters.html',
  styleUrl: './terms-parameters.css',
})
export class TermsParameters implements OnInit, OnChanges, OnDestroy {
  readonly yesNoOptions: ReadonlyArray<{
    label: string;
    value: BinaryValue;
  }> = [
    { label: 'COMMON.YES', value: 1 },
    { label: 'COMMON.NO', value: 0 },
  ];

  readonly kiborDayOptions: number[] = Array.from(
    { length: KIBOR_DAY_MAX },
    (_, index) => index + KIBOR_DAY_MIN,
  );

  @Input() config: TermsParametersConfig | null = null;
  @Input() editable = false;
  @Input() crdtPrpslSeq: number | null = null;
  @Input() sectionId = '';

  readonly repaymentTermsArray = new FormArray<
    FormGroup<RepaymentTermFormControls>
  >([]);

  readonly globalDetailsForm = new FormGroup<GlobalDetailsFormControls>(
    {
      repaymentStartDate: new FormControl<string | null>(null, {
        validators: [Validators.required],
      }),
      repaymentEndDate: new FormControl<string | null>(null, {
        validators: [Validators.required],
      }),
      installmentFrequency: new FormControl<number | null>(null, {
        validators: [Validators.required],
      }),
      creditEnsuranceFee: new FormControl<number | null>(null, {
        validators: [
          Validators.min(0),
          Validators.max(0.99),
          decimalNumberValidator(),
        ],
      }),
      gracePeriod: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.min(0), Validators.max(999)],
      }),
      numberOfInstallments: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.min(1), Validators.max(999)],
      }),
      proposedExposure: new FormControl<number | null>(null, {
        validators: [
          Validators.required,
          Validators.min(0),
          decimalNumberValidator(),
          maxProposedExposureValidator(() => this.totalDisbursementAmount),
        ],
      }),
      inArrears: new FormControl<BinaryValue>(0, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      upfrontCharges: new FormControl<BinaryValue>(0, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      upfrontChargesPercentage: new FormControl<number | null>({
        value: null,
        disabled: true,
      }),
    },
    {
      validators: [
        dateRangeValidator(
          'repaymentStartDate',
          'repaymentEndDate',
          'repaymentDateRange',
        ),
      ],
    },
  );

  termsMeta: RepaymentTermMeta[] = [];

  /** Sum of disbursement amounts across all repayment terms (Terms Finance + Islamic Finance). */
  get totalDisbursementAmount(): number {
    return roundToTwoDecimals(
      this.termsMeta.reduce(
        (total, meta) =>
          total + toNumber(meta.group.controls.disbursementAmount.value, 0),
        0,
      ),
    );
  }

  termOptions: DropdownOption[] = [];
  guarantorOptions: DropdownOption[] = [];
  private guarantorsLoading = false;
  private guarantorsLoaded = false;
  frequencyOptions: DropdownOption[] = [];
  kiborResetPeriodOptions: DropdownOption[] = [];

  loading = false;
  loadingSavedData = false;
  saving = false;
  saveSuccess = false;
  saveError = '';

  showAddTermPopup = false;
  draftTermTypeControl = new FormControl<number | null>(null, {
    validators: [Validators.required],
  });
  draftMeta: RepaymentTermMeta | null = null;
  draftError = '';

  private readonly subscriptions = new Subscription();
  private loadedForProposalSequence: number | null = null;
  private removedTermRows: CreditTermParameterDto[] = [];

  constructor(
    private readonly pickListService: PickListService,
    private readonly creditService: CraditService,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadRequiredPicklists();
    this.listenForSectionSave();

    const globalUpfrontSubscription =
      this.globalDetailsForm.controls.upfrontCharges.valueChanges.subscribe(
        (value) => {
          this.applyGlobalUpfrontCharges(value === 1, true);
        },
      );
    this.subscriptions.add(globalUpfrontSubscription);

    const draftTermTypeSubscription =
      this.draftTermTypeControl.valueChanges.subscribe((value) => {
        if (!this.draftMeta) {
          return;
        }

        this.draftMeta.termRefCdSeq = Number(value) || 0;
      });
    this.subscriptions.add(draftTermTypeSubscription);

    this.applyControlStates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['config'] &&
      !changes['config'].firstChange &&
      this.termOptions.length
    ) {
      this.rebuildFromConfig();
    }

    if (
      changes['crdtPrpslSeq'] &&
      !changes['crdtPrpslSeq'].firstChange
    ) {
      this.loadedForProposalSequence = null;
      this.loadSavedTermsParameters();
    }

    if (changes['editable'] && !changes['editable'].firstChange) {
      this.applyControlStates();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.termsMeta.forEach((meta) => meta.subscriptions.unsubscribe());
    this.draftMeta?.subscriptions.unsubscribe();
  }

  get isSaveDisabled(): boolean {
    return (
      !this.editable ||
      this.saving ||
      this.loading ||
      this.repaymentTermsArray.length === 0 ||
      this.repaymentTermsArray.invalid ||
      this.globalDetailsForm.invalid ||
      this.isRepaymentBeforeDisbursement
    );
  }

  readonly blockManualDateEntry = blockManualDateEntryUtil;

  get earliestDisbursementStartDate(): string | null {
    const dates = this.termsMeta
      .map((meta) => meta.group.controls.disbursementStartDate.value)
      .filter((value): value is string => !!value)
      .map((value) => parseLocalDate(value))
      .filter((value): value is Date => value !== null);

    if (!dates.length) {
      return null;
    }

    return formatDateOnly(
      dates.reduce((earliest, date) => (date < earliest ? date : earliest)),
    );
  }

  get isRepaymentBeforeDisbursement(): boolean {
    const repaymentStart =
      this.globalDetailsForm.controls.repaymentStartDate.value;
    const earliest = this.earliestDisbursementStartDate;

    if (!repaymentStart || !earliest) {
      return false;
    }

    const repaymentDate = parseLocalDate(repaymentStart);
    const earliestDate = parseLocalDate(earliest);

    return (
      !!repaymentDate && !!earliestDate && repaymentDate < earliestDate
    );
  }

  get availableTermOptions(): DropdownOption[] {
    const existingIds = new Set(
      this.termsMeta.map((meta) => meta.termRefCdSeq),
    );

    // Facility type 30 is never offered as a new repayment term.
    return this.termOptions.filter(
      (option) =>
        Number(option.value) !== HIDDEN_TERM_TYPE &&
        !existingIds.has(Number(option.value)),
    );
  }

  trackByTerm(_index: number, meta: RepaymentTermMeta): number {
    return meta.termRefCdSeq;
  }

  guarantorsArrayOf(
    meta: RepaymentTermMeta,
  ): FormArray<FormGroup<GuarantorFormControls>> {
    return meta.group.controls.guarantors;
  }

  guarantorGroupOf(
    meta: RepaymentTermMeta,
    guarantorIndex: number,
  ): FormGroup<GuarantorFormControls> {
    return this.guarantorsArrayOf(meta).at(guarantorIndex);
  }

  termLabel(termRefCdSeq: number): string {
    return (
      this.termOptions.find(
        (option) => Number(option.value) === Number(termRefCdSeq),
      )?.label ??
      this.translate.instant('TERMS_PARAMS.TERM_FALLBACK', { seq: termRefCdSeq })
    );
  }

  guarantorLabel(guarantorRefCdSeq: number): string {
    return (
      this.guarantorOptions.find(
        (option) =>
          Number(option.value) === Number(guarantorRefCdSeq),
      )?.label ??
      this.translate.instant('TERMS_PARAMS.GUARANTOR_FALLBACK', {
        seq: guarantorRefCdSeq,
      })
    );
  }

  availableGuarantorsForTerm(meta: RepaymentTermMeta): DropdownOption[] {
    const selectedIds = new Set(
      this.guarantorsArrayOf(meta).controls.map((control) =>
        Number(control.controls.guarantorRefCdSeq.value),
      ),
    );

    return this.guarantorOptions.filter(
      (option) => !selectedIds.has(Number(option.value)),
    );
  }

  guarantorTotalForTerm(meta: RepaymentTermMeta): number {
    return roundToTwoDecimals(
      this.guarantorsArrayOf(meta).controls.reduce(
        (total, control) =>
          total + toNumber(control.controls.percentage.value, 0),
        0,
      ),
    );
  }

  isGuarantorTotalExceeded(meta: RepaymentTermMeta): boolean {
    return this.guarantorTotalForTerm(meta) > 100;
  }

  remainingGuarantorPercentage(meta: RepaymentTermMeta): number {
    return roundToTwoDecimals(100 - this.guarantorTotalForTerm(meta));
  }

  addGuarantorsToTerm(meta: RepaymentTermMeta): void {
    if (
      !this.editable ||
      this.saving ||
      meta.guarantorSelectionControl.value.length === 0
    ) {
      return;
    }

    const guarantorsArray = this.guarantorsArrayOf(meta);
    const existingIds = new Set(
      guarantorsArray.controls.map((control) =>
        Number(control.controls.guarantorRefCdSeq.value),
      ),
    );

    meta.guarantorSelectionControl.value
      .map(Number)
      .filter((id) => !existingIds.has(id))
      .forEach((id) => {
        guarantorsArray.push(
          this.buildGuarantorFormGroup({
            crdtGrntrSeq: null,
            guarantorRefCdSeq: id,
            percentage: null,
          }),
        );
      });

    meta.guarantorSelectionControl.setValue([]);
  }

  removeGuarantorFromTerm(
    meta: RepaymentTermMeta,
    guarantorIndex: number,
  ): void {
    if (!this.editable || this.saving) {
      return;
    }

    const guarantorsArray = this.guarantorsArrayOf(meta);
    const group = guarantorsArray.at(guarantorIndex);
    const crdtGrntrSeq = group.controls.crdtGrntrSeq.value;

    if (crdtGrntrSeq !== null) {
      meta.removedGuarantors.push({
        crdtGrntrSeq,
        grntrKey: group.controls.guarantorRefCdSeq.value,
        share: 0,
        delFlg: 1,
      });
    }

    guarantorsArray.removeAt(guarantorIndex);
  }

  removeTermSection(meta: RepaymentTermMeta): void {
    if (!this.editable || this.saving) {
      return;
    }

    const index = this.termsMeta.indexOf(meta);

    if (index === -1) {
      return;
    }

    this.removeTermAt(index);
  }

  isTermControlInvalid(
    meta: RepaymentTermMeta,
    controlName: keyof RepaymentTermFormControls,
  ): boolean {
    const control = meta.group.controls[controlName] as AbstractControl;

    return !!(control.invalid && (control.dirty || control.touched));
  }

  getTermControlError(
    meta: RepaymentTermMeta,
    controlName: keyof RepaymentTermFormControls,
    label: string,
  ): string {
    const control = meta.group.controls[controlName] as AbstractControl;

    return getValidationMessage(control.errors, label, this.translate);
  }

  hasTermError(meta: RepaymentTermMeta, errorKey: string): boolean {
    return meta.group.hasError(errorKey);
  }

  isGuarantorPercentageInvalid(
    meta: RepaymentTermMeta,
    guarantorIndex: number,
  ): boolean {
    const control = this.guarantorGroupOf(meta, guarantorIndex).controls
      .percentage;

    return !!(control.invalid && (control.dirty || control.touched));
  }

  getGuarantorPercentageError(
    meta: RepaymentTermMeta,
    guarantorIndex: number,
  ): string {
    const control = this.guarantorGroupOf(meta, guarantorIndex).controls
      .percentage;

    return getValidationMessage(
      control.errors,
      'TERMS_PARAMS.GUARANTOR_PERCENTAGE',
      this.translate,
    );
  }

  openAddTermPopup(): void {
    if (!this.editable || this.saving || !this.availableTermOptions.length) {
      return;
    }

    const group = this.buildTermFormGroup(null);

    const meta: RepaymentTermMeta = {
      termRefCdSeq: 0,
      crdtTrmParamsSeq: null,
      guarantorSelectionControl: new FormControl<number[]>([], {
        nonNullable: true,
      }),
      subscriptions: new Subscription(),
      group,
      removedGuarantors: [],
    };

    this.registerTermSubscriptions(meta);
    this.applyKiborVariable(
      meta,
      group.controls.kiborVariable.value === 1,
      false,
    );
    this.applyRenewable(
      meta,
      group.controls.isRenewable.value === 1,
      false,
    );
    this.applyPrime(
      meta,
      group.controls.isPrime.value === 1,
      false,
    );
    this.applyGuarantorFlag(
      meta,
      group.controls.guarantorFlag.value === 1,
      true,
    );

    this.draftTermTypeControl.reset(null);
    this.draftError = '';
    this.draftMeta = meta;
    this.showAddTermPopup = true;
  }

  confirmAddTerm(): void {
    if (!this.draftMeta) {
      return;
    }

    this.draftTermTypeControl.markAsTouched();
    this.draftMeta.group.markAllAsTouched();
    this.draftMeta.group.updateValueAndValidity();

    if (this.draftTermTypeControl.invalid) {
      this.draftError = 'TERMS_PARAMS.SELECT_TERM_TO_CONTINUE';
      return;
    }

    if (this.draftMeta.group.invalid) {
      this.draftError = 'TERMS_PARAMS.CORRECT_HIGHLIGHTED_FIELDS';
      return;
    }

    this.draftMeta.termRefCdSeq = Number(this.draftTermTypeControl.value);

    this.repaymentTermsArray.push(this.draftMeta.group);
    this.termsMeta.push(this.draftMeta);

    this.draftMeta = null;
    this.draftError = '';
    this.showAddTermPopup = false;

    this.globalDetailsForm.controls.proposedExposure.updateValueAndValidity({
      emitEvent: false,
    });
  }

  onAddTermPopupVisibilityChange(visible: boolean): void {
    this.showAddTermPopup = visible;

    if (!visible) {
      this.closeAddTermPopup();
    }
  }

  closeAddTermPopup(): void {
    this.draftMeta?.subscriptions.unsubscribe();
    this.draftMeta = null;
    this.draftError = '';
    this.showAddTermPopup = false;
  }

  saveTermsParameters(): void {
    if (this.saving) {
      return;
    }

    this.saveSuccess = false;
    this.saveError = '';

    const proposalSequence = this.getProposalSequence();

    if (proposalSequence <= 0) {
      this.saveError = 'TERMS_PARAMS.PROPOSAL_SEQ_NA';
      this.toast.error(this.saveError);
      this.reportValidationError(this.saveError);
      return;
    }

    this.repaymentTermsArray.markAllAsTouched();
    this.repaymentTermsArray.updateValueAndValidity();
    this.globalDetailsForm.markAllAsTouched();
    this.globalDetailsForm.updateValueAndValidity();

    if (this.repaymentTermsArray.length === 0) {
      this.saveError = 'TERMS_PARAMS.ADD_AT_LEAST_ONE_TERM';
      this.toast.error(this.saveError);
      this.reportValidationError(this.saveError);
      return;
    }

    if (this.repaymentTermsArray.invalid || this.globalDetailsForm.invalid) {
      this.saveError = 'TERMS_PARAMS.CORRECT_ALL_ERRORS';
      this.toast.error(this.saveError);
      this.reportValidationError(this.saveError);
      return;
    }

    if (this.isRepaymentBeforeDisbursement) {
      this.saveError = 'TERMS_PARAMS.REPAYMENT_BEFORE_DISBURSEMENT';
      this.toast.error(this.saveError);
      this.reportValidationError(this.saveError);
      return;
    }

    const payload = this.buildSavePayload(proposalSequence);

    this.saving = true;
    this.applyControlStates();

    const subscription = this.creditService
      .saveTermParameters(payload)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.applyControlStates();
        }),
      )
      .subscribe({
        next: (response: unknown) => {
          // The API can answer 200 with `{ error: "..." }` when the save was
          // rejected (e.g. "Credit ensure fee must be greater than 0") —
          // that is a failure: do not mark it saved or move on. The global
          // ErrorInterceptor already shows its toast, so none is shown here.
          const embeddedError = (response as { error?: unknown } | null)?.error;

          if (typeof embeddedError === 'string' && embeddedError.trim()) {
            this.saveError = embeddedError.trim();

            this.creditService.reportSectionSaveResult({
              sectionId: this.sectionId,
              status: 'api-error',
              message: this.saveError,
            });
            return;
          }

          this.saveSuccess = true;
          this.applySavedResponse(response, payload);
          this.toast.success('TERMS_PARAMS.SAVED_SUCCESS');

          this.creditService.reportSectionSaveResult({
            sectionId: this.sectionId,
            status: 'saved',
          });
        },
        error: (error: unknown) => {
          console.error('Unable to save Terms Parameters:', error);
          this.saveError = extractErrorMessage(
            error,
            'TERMS_PARAMS.UNABLE_SAVE',
          );
          this.toast.error(this.saveError);

          this.creditService.reportSectionSaveResult({
            sectionId: this.sectionId,
            status: 'api-error',
            message: this.saveError ? this.translate.instant(this.saveError) : this.saveError,
          });
        },
      });

    this.subscriptions.add(subscription);
  }

  private loadRequiredPicklists(): void {
    this.loading = true;

    const subscription = this.pickListService
      .loadCodesAsync([
        PKL_GRP.FACILITY_TYPE,
        PKL_GRP.DISBURSEMENT_FREQUENCY,
        PKL_GRP.KIBOR_RESET_PERIOD,
      ])
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: () => {
          // Repayment term types come from ref code group 13 (FACILITY_TYPE).
          this.termOptions = sortOptions(
            this.pickListService.getOptionsSync(PKL_GRP.FACILITY_TYPE),
          );
          this.frequencyOptions = sortOptions(
            this.pickListService.getOptionsSync(
              PKL_GRP.DISBURSEMENT_FREQUENCY,
            ),
          );
          this.kiborResetPeriodOptions = sortOptions(
            this.pickListService.getOptionsSync(
              PKL_GRP.KIBOR_RESET_PERIOD,
            ),
          );

          this.rebuildFromConfig();
          this.loadSavedTermsParameters();
        },
        error: (error: unknown) => {
          console.error(
            'Unable to load Terms Parameters picklists:',
            error,
          );

          this.saveError = 'TERMS_PARAMS.UNABLE_LOAD_PICKLISTS';
        },
      });

    this.subscriptions.add(subscription);
  }

  /** Loads active guarantors from setup once; skipped while loading or already loaded. */
  private loadActiveGuarantors(): void {
    if (this.guarantorsLoading || this.guarantorsLoaded) {
      return;
    }

    this.guarantorsLoading = true;

    const subscription = this.creditService
      .getActiveGuarantorList()
      .pipe(
        finalize(() => {
          this.guarantorsLoading = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          const rows: Record<string, any>[] = Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
              ? response.data
              : [];

          this.guarantorOptions = sortOptions(
            rows
              .filter((row) => row?.['stpGrntrSeq'] != null)
              .map((row) => ({
                value: Number(row['stpGrntrSeq']),
                label: String(row['grntrNm'] ?? ''),
              })),
          );
          this.guarantorsLoaded = true;
        },
        error: (error: unknown) => {
          console.error('Unable to load active guarantors:', error);
          this.toast.error(
            extractErrorMessage(error, 'TERMS_PARAMS.UNABLE_LOAD_PICKLISTS'),
          );
        },
      });

    this.subscriptions.add(subscription);
  }

  private listenForSectionSave(): void {
    const subscription = this.creditService.saveSectionTrigger$
      .pipe(
        filter(
          (sectionId: string) => sectionId === this.sectionId,
        ),
      )
      .subscribe(() => {
        this.saveTermsParameters();
      });

    this.subscriptions.add(subscription);
  }

  private reportValidationError(message: string): void {
    this.creditService.reportSectionSaveResult({
      sectionId: this.sectionId,
      status: 'validation-error',
      message: this.translate.instant(message),
    });
  }

  private loadSavedTermsParameters(): void {
    const proposalSequence = this.getProposalSequence();

    if (
      proposalSequence <= 0 ||
      this.loadedForProposalSequence === proposalSequence
    ) {
      return;
    }

    if (Array.isArray(this.config?.crdtTrmParamsLst)) {
      this.loadedForProposalSequence = proposalSequence;
      return;
    }

    this.loadingSavedData = true;

    const subscription = this.creditService
      .getCreditTermParameters(proposalSequence)
      .pipe(
        finalize(() => {
          this.loadingSavedData = false;
        }),
      )
      .subscribe({
        next: (response: unknown) => {
          this.loadedForProposalSequence = proposalSequence;
          this.config = extractSavedConfig(response) ?? {
            crdtTrmParamsLst: [],
          };
          this.rebuildFromConfig();
        },
        error: (error: unknown) => {
          console.error(
            'Unable to load saved Terms Parameters:',
            error,
          );

          this.saveError = 'TERMS_PARAMS.UNABLE_LOAD_SAVED';
        },
      });

    this.subscriptions.add(subscription);
  }

  private rebuildFromConfig(): void {
    this.clearAllTermSections();

    this.getConfigRows()
      .filter((row) => Number(row?.['delFlg'] ?? 0) !== 1)
      .forEach((row) => {
        const termRefCdSeq = Number(row?.['typ']);

        if (!Number.isFinite(termRefCdSeq) || termRefCdSeq <= 0) {
          return;
        }

        this.addTermSection(termRefCdSeq, row);
      });

    this.patchGlobalDetails();
    this.applyControlStates();
  }

  private patchGlobalDetails(): void {
    const upfrontCharges: BinaryValue =
      Number(this.config?.upfrntFlg) === 1 ? 1 : 0;

    this.globalDetailsForm.patchValue(
      {
        repaymentStartDate: this.toDateString(this.config?.rpStrtDt),
        repaymentEndDate: this.toDateString(this.config?.rpEndDt),
        installmentFrequency: this.toNullableNumber(
          this.config?.rpymntFrqncy,
        ),
        creditEnsuranceFee: this.toNullableNumber(
          this.config?.crdtEnsrFee,
        ),
        gracePeriod: this.toNullableNumber(this.config?.grcPrd),
        numberOfInstallments: this.toNullableNumber(
          this.config?.noOfInstlmnt,
        ),
        proposedExposure: this.toNullableNumber(
          this.config?.prpsdExpsr,
        ),
        inArrears: Number(this.config?.arrearFlg) === 1 ? 1 : 0,
        upfrontCharges,
        upfrontChargesPercentage: this.toNullableNumber(
          this.config?.upFrntFee,
        ),
      },
      { emitEvent: false },
    );

    this.applyGlobalUpfrontCharges(upfrontCharges === 1, false);
  }

  private clearAllTermSections(): void {
    this.termsMeta.forEach((meta) => meta.subscriptions.unsubscribe());
    this.termsMeta = [];
    this.repaymentTermsArray.clear();
    this.removedTermRows = [];
  }

  private getConfigRows(): Record<string, any>[] {
    return Array.isArray(this.config?.crdtTrmParamsLst)
      ? (this.config!.crdtTrmParamsLst as Record<string, any>[])
      : [];
  }

  private removeTermAt(index: number): void {
    const meta = this.termsMeta[index];

    if (!meta) {
      return;
    }

    meta.subscriptions.unsubscribe();

    if (meta.crdtTrmParamsSeq !== null) {
      this.removedTermRows.push({
        crdtTrmParamsSeq: meta.crdtTrmParamsSeq,
        typ: meta.termRefCdSeq,
        dsbmntAmount: 0,
        rnwAblAmnt: 0,
        rnwAblFlg: 0,
        prmAmnt: 0,
        prmFlg: 0,
        vkbrFlg: 0,
        vKbrStrt: 0,
        kbrRstPrd: 0,
        sprdRt: 0,
        grntrFlg: 0,
        crdtTrmParamsGrntrList: [],
        delFlg: 1,
        dsbStrtDt: null,
        dsbEndDt: null,
      });
    }

    this.repaymentTermsArray.removeAt(index);
    this.termsMeta.splice(index, 1);

    this.globalDetailsForm.controls.proposedExposure.updateValueAndValidity({
      emitEvent: false,
    });
  }

  private addTermSection(
    termRefCdSeq: number,
    savedRow: Record<string, any> | null,
  ): void {
    const group = this.buildTermFormGroup(savedRow);

    const meta: RepaymentTermMeta = {
      termRefCdSeq,
      crdtTrmParamsSeq: this.toNullableNumber(
        savedRow?.['crdtTrmParamsSeq'],
      ),
      guarantorSelectionControl: new FormControl<number[]>([], {
        nonNullable: true,
      }),
      subscriptions: new Subscription(),
      group,
      removedGuarantors: [],
    };

    this.repaymentTermsArray.push(group);
    this.termsMeta.push(meta);

    this.registerTermSubscriptions(meta);
    this.applyKiborVariable(
      meta,
      group.controls.kiborVariable.value === 1,
      false,
    );
    this.applyRenewable(
      meta,
      group.controls.isRenewable.value === 1,
      false,
    );
    this.applyPrime(
      meta,
      group.controls.isPrime.value === 1,
      false,
    );
    this.applyGuarantorFlag(
      meta,
      group.controls.guarantorFlag.value === 1,
      true,
    );

    this.globalDetailsForm.controls.proposedExposure.updateValueAndValidity({
      emitEvent: false,
    });
  }

  private buildTermFormGroup(
    savedRow: Record<string, any> | null,
  ): FormGroup<RepaymentTermFormControls> {
    const disbursementAmount = this.toNullableNumber(
      savedRow?.['dsbmntAmount'],
    );
    const disbursementStartDate = this.toDateString(
      savedRow?.['dsbStrtDt'],
    );
    const disbursementEndDate = this.toDateString(savedRow?.['dsbEndDt']);
    const kiborDay = this.toNullableNumber(savedRow?.['vKbrStrt']);
    const kiborResetPeriod = this.toNullableNumber(
      savedRow?.['kbrRstPrd'],
    );
    const kiborSpreadRate = this.toNullableNumber(savedRow?.['sprdRt']);
    const renewableAmount = this.toNullableNumber(
      savedRow?.['rnwAblAmnt'],
    );

    const kiborVariable: BinaryValue =
      Number(savedRow?.['vkbrFlg']) === 1 ? 1 : 0;
    const isRenewable: BinaryValue =
      savedRow?.['rnwAblFlg'] !== undefined &&
      savedRow?.['rnwAblFlg'] !== null
        ? Number(savedRow['rnwAblFlg']) === 1
          ? 1
          : 0
        : renewableAmount !== null && renewableAmount > 0
          ? 1
          : 0;
    const primeAmount = this.toNullableNumber(savedRow?.['prmAmnt']);
    const isPrime: BinaryValue =
      savedRow?.['prmFlg'] !== undefined && savedRow?.['prmFlg'] !== null
        ? Number(savedRow['prmFlg']) === 1
          ? 1
          : 0
        : primeAmount !== null && primeAmount > 0
          ? 1
          : 0;
    const guarantorFlag: BinaryValue =
      Number(savedRow?.['grntrFlg']) === 1 ? 1 : 0;

    const guarantorRows: Record<string, any>[] = Array.isArray(
      savedRow?.['crdtTrmParamsGrntrList'],
    )
      ? (savedRow!['crdtTrmParamsGrntrList'] as Record<string, any>[])
      : [];

    return new FormGroup<RepaymentTermFormControls>(
      {
        disbursementAmount: new FormControl<number | null>(
          disbursementAmount,
          { validators: [Validators.required, positiveDecimalValidator()] },
        ),
        disbursementStartDate: new FormControl<string | null>(
          disbursementStartDate,
          { validators: [Validators.required] },
        ),
        disbursementEndDate: new FormControl<string | null>(
          disbursementEndDate,
          { validators: [Validators.required] },
        ),

        kiborVariable: new FormControl<BinaryValue>(kiborVariable, {
          nonNullable: true,
          validators: [Validators.required],
        }),
        kiborDay: new FormControl<number | null>({
          value: kiborDay,
          disabled: true,
        }),
        kiborResetPeriod: new FormControl<number | null>({
          value: kiborResetPeriod,
          disabled: true,
        }),
        kiborSpreadRate: new FormControl<number | null>(kiborSpreadRate, {
          validators: [
            Validators.required,
            Validators.min(0),
            Validators.max(100),
            decimalNumberValidator(),
          ],
        }),

        guarantorFlag: new FormControl<BinaryValue>(guarantorFlag, {
          nonNullable: true,
          validators: [Validators.required],
        }),
        guarantors: new FormArray<FormGroup<GuarantorFormControls>>(
          guarantorRows
            .filter((row) => Number(row?.['delFlg'] ?? 0) !== 1)
            .map((row) =>
              this.buildGuarantorFormGroup({
                crdtGrntrSeq: this.toNullableNumber(
                  row?.['crdtGrntrSeq'],
                ),
                guarantorRefCdSeq: Number(row?.['grntrKey']),
                percentage: this.toNullableNumber(row?.['share']),
              }),
            ),
        ),

        isRenewable: new FormControl<BinaryValue>(isRenewable, {
          nonNullable: true,
          validators: [Validators.required],
        }),
        renewableAmount: new FormControl<number | null>({
          value: renewableAmount,
          disabled: true,
        }),

        isPrime: new FormControl<BinaryValue>(isPrime, {
          nonNullable: true,
          validators: [Validators.required],
        }),
        primeAmount: new FormControl<number | null>({
          value: primeAmount,
          disabled: true,
        }),
      },
      {
        validators: [
          dateRangeValidator(
            'disbursementStartDate',
            'disbursementEndDate',
            'disbursementDateRange',
          ),
          maxAmountValidator(
            'renewableAmount',
            'disbursementAmount',
            'renewableAmountExceedsDisbursement',
          ),
          maxAmountValidator(
            'primeAmount',
            'disbursementAmount',
            'primeAmountExceedsDisbursement',
          ),
          guarantorTotalValidator(),
        ],
      },
    );
  }

  private buildGuarantorFormGroup(allocation: {
    crdtGrntrSeq: number | null;
    guarantorRefCdSeq: number;
    percentage: number | null;
  }): FormGroup<GuarantorFormControls> {
    return new FormGroup<GuarantorFormControls>({
      crdtGrntrSeq: new FormControl<number | null>(allocation.crdtGrntrSeq),
      guarantorRefCdSeq: new FormControl<number>(
        Number(allocation.guarantorRefCdSeq),
        { nonNullable: true },
      ),
      percentage: new FormControl<number | null>(
        allocation.percentage ?? null,
        {
          // A guarantor's share must be above 0 (0 or negative is invalid).
          validators: [
            Validators.required,
            positiveDecimalValidator(),
            Validators.max(100),
            decimalNumberValidator(),
          ],
        },
      ),
    });
  }

  private registerTermSubscriptions(meta: RepaymentTermMeta): void {
    const kiborSubscription =
      meta.group.controls.kiborVariable.valueChanges.subscribe((value) => {
        this.applyKiborVariable(meta, value === 1, true);
      });

    const renewableSubscription =
      meta.group.controls.isRenewable.valueChanges.subscribe((value) => {
        this.applyRenewable(meta, value === 1, true);
      });

    const primeSubscription =
      meta.group.controls.isPrime.valueChanges.subscribe((value) => {
        this.applyPrime(meta, value === 1, true);
      });

    const guarantorFlagSubscription =
      meta.group.controls.guarantorFlag.valueChanges.subscribe((value) => {
        this.applyGuarantorFlag(meta, value === 1, true);
      });

    const disbursementAmountSubscription =
      meta.group.controls.disbursementAmount.valueChanges.subscribe(() => {
        this.globalDetailsForm.controls.proposedExposure.updateValueAndValidity({
          emitEvent: false,
        });
      });

    meta.subscriptions.add(kiborSubscription);
    meta.subscriptions.add(renewableSubscription);
    meta.subscriptions.add(primeSubscription);
    meta.subscriptions.add(guarantorFlagSubscription);
    meta.subscriptions.add(disbursementAmountSubscription);
  }

  private applyKiborVariable(
    meta: RepaymentTermMeta,
    enabled: boolean,
    clearValues: boolean,
  ): void {
    const dayControl = meta.group.controls.kiborDay;
    const resetPeriodControl = meta.group.controls.kiborResetPeriod;

    if (enabled) {
      dayControl.setValidators([
        Validators.required,
        Validators.min(KIBOR_DAY_MIN),
        Validators.max(KIBOR_DAY_MAX),
      ]);
      resetPeriodControl.setValidators([Validators.required]);

      dayControl.enable({ emitEvent: false });
      resetPeriodControl.enable({ emitEvent: false });
    } else {
      dayControl.clearValidators();
      resetPeriodControl.clearValidators();

      if (clearValues) {
        dayControl.reset(null, { emitEvent: false });
        resetPeriodControl.reset(null, { emitEvent: false });
      }

      dayControl.disable({ emitEvent: false });
      resetPeriodControl.disable({ emitEvent: false });
    }

    dayControl.updateValueAndValidity({ emitEvent: false });
    resetPeriodControl.updateValueAndValidity({ emitEvent: false });
  }

  private applyRenewable(
    meta: RepaymentTermMeta,
    enabled: boolean,
    clearValues: boolean,
  ): void {
    const control = meta.group.controls.renewableAmount;

    if (enabled) {
      control.setValidators([Validators.required, positiveDecimalValidator()]);
      control.enable({ emitEvent: false });
    } else {
      control.clearValidators();

      if (clearValues) {
        control.reset(null, { emitEvent: false });
      }

      control.disable({ emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private applyPrime(
    meta: RepaymentTermMeta,
    enabled: boolean,
    clearValues: boolean,
  ): void {
    const control = meta.group.controls.primeAmount;

    if (enabled) {
      control.setValidators([Validators.required, positiveDecimalValidator()]);
      control.enable({ emitEvent: false });
    } else {
      control.clearValidators();

      if (clearValues) {
        control.reset(null, { emitEvent: false });
      }

      control.disable({ emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private applyGuarantorFlag(
    meta: RepaymentTermMeta,
    enabled: boolean,
    clearValues: boolean,
  ): void {
    if (enabled) {
      this.loadActiveGuarantors();
      return;
    }

    if (!clearValues) {
      return;
    }

    const guarantorsArray = this.guarantorsArrayOf(meta);

    guarantorsArray.controls.forEach((group) => {
      const crdtGrntrSeq = group.controls.crdtGrntrSeq.value;

      if (crdtGrntrSeq !== null) {
        meta.removedGuarantors.push({
          crdtGrntrSeq,
          grntrKey: group.controls.guarantorRefCdSeq.value,
          share: 0,
          delFlg: 1,
        });
      }
    });

    guarantorsArray.clear();
    meta.guarantorSelectionControl.setValue([], { emitEvent: false });
  }

  private applyGlobalUpfrontCharges(
    enabled: boolean,
    clearValues: boolean,
  ): void {
    const control = this.globalDetailsForm.controls.upfrontChargesPercentage;

    if (enabled) {
      control.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(100),
        decimalNumberValidator(),
      ]);
      control.enable({ emitEvent: false });
    } else {
      control.clearValidators();

      if (clearValues) {
        control.reset(null, { emitEvent: false });
      }

      control.disable({ emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private applyControlStates(): void {
    if (!this.editable || this.saving) {
      this.repaymentTermsArray.disable({ emitEvent: false });
      this.globalDetailsForm.disable({ emitEvent: false });
      this.termsMeta.forEach((meta) =>
        meta.guarantorSelectionControl.disable({ emitEvent: false }),
      );
      return;
    }

    this.repaymentTermsArray.enable({ emitEvent: false });
    this.globalDetailsForm.enable({ emitEvent: false });
    this.termsMeta.forEach((meta) =>
      meta.guarantorSelectionControl.enable({ emitEvent: false }),
    );

    this.termsMeta.forEach((meta) => {
      this.applyKiborVariable(
        meta,
        meta.group.controls.kiborVariable.value === 1,
        false,
      );
      this.applyRenewable(
        meta,
        meta.group.controls.isRenewable.value === 1,
        false,
      );
      this.applyPrime(
        meta,
        meta.group.controls.isPrime.value === 1,
        false,
      );
    });

    this.applyGlobalUpfrontCharges(
      this.globalDetailsForm.controls.upfrontCharges.value === 1,
      false,
    );
  }

  private buildSavePayload(
    proposalSequence: number,
  ): SaveCreditTermParametersPayload {
    const globalRaw = this.globalDetailsForm.getRawValue();
    const upFrntFee =
      globalRaw.upfrontCharges === 1
        ? toNumber(globalRaw.upfrontChargesPercentage, 0)
        : 0;

    const rows: CreditTermParameterDto[] = this.termsMeta.map((meta) => {
      const raw = meta.group.getRawValue();

      const guarantorRows: CreditTermParameterGuarantorDto[] = raw.guarantors.map(
        (guarantor) => ({
          crdtGrntrSeq: guarantor.crdtGrntrSeq,
          grntrKey: guarantor.guarantorRefCdSeq,
          share: toNumber(guarantor.percentage, 0),
          delFlg: 0,
        }),
      );

      guarantorRows.push(...meta.removedGuarantors);

      return {
        crdtTrmParamsSeq: meta.crdtTrmParamsSeq,
        typ: meta.termRefCdSeq,
        dsbmntAmount: toNumber(raw.disbursementAmount, 0),
        rnwAblAmnt:
          raw.isRenewable === 1 ? toNumber(raw.renewableAmount, 0) : 0,
        rnwAblFlg: raw.isRenewable,
        prmAmnt: raw.isPrime === 1 ? toNumber(raw.primeAmount, 0) : 0,
        prmFlg: raw.isPrime,
        vkbrFlg: raw.kiborVariable,
        vKbrStrt: raw.kiborVariable === 1 ? toNumber(raw.kiborDay, 0) : 0,
        kbrRstPrd:
          raw.kiborVariable === 1 ? toNumber(raw.kiborResetPeriod, 0) : 0,
        sprdRt: toNumber(raw.kiborSpreadRate, 0),
        grntrFlg: raw.guarantorFlag,
        crdtTrmParamsGrntrList: guarantorRows,
        delFlg: 0,
        dsbStrtDt: raw.disbursementStartDate,
        dsbEndDt: raw.disbursementEndDate,
      };
    });

    rows.push(...this.removedTermRows);

    return {
      crdtPrpslSeq: proposalSequence,
      rpStrtDt: globalRaw.repaymentStartDate,
      rpEndDt: globalRaw.repaymentEndDate,
      rpymntFrqncy: toNumber(globalRaw.installmentFrequency, 0),
      sprdRt: 0,
      grcPrd: toNumber(globalRaw.gracePeriod, 0),
      noOfInstlmnt: toNumber(globalRaw.numberOfInstallments, 0),
      prpsdExpsr: toNumber(globalRaw.proposedExposure, 0),
      arrearFlg: globalRaw.inArrears,
      upfrntFlg: globalRaw.upfrontCharges,
      upFrntFee,
      crdtEnsrFee: toNumber(globalRaw.creditEnsuranceFee, 0),
      crdtTrmParamsLst: rows,
    };
  }

  private applySavedResponse(
    response: unknown,
    payload: SaveCreditTermParametersPayload,
  ): void {
    const savedRows = extractSavedConfig(response)?.crdtTrmParamsLst ?? [];

    this.termsMeta.forEach((meta) => {
      const matchedSaved = savedRows.find(
        (row) => Number(row?.['typ']) === meta.termRefCdSeq,
      );

      const sequence = this.toNullableNumber(
        matchedSaved?.['crdtTrmParamsSeq'],
      );

      if (sequence !== null) {
        meta.crdtTrmParamsSeq = sequence;
      }

      const savedGuarantorRows: Record<string, any>[] = Array.isArray(
        matchedSaved?.['crdtTrmParamsGrntrList'],
      )
        ? (matchedSaved!['crdtTrmParamsGrntrList'] as Record<string, any>[])
        : [];

      this.guarantorsArrayOf(meta).controls.forEach((group) => {
        if (group.controls.crdtGrntrSeq.value !== null) {
          return;
        }

        const matchedGuarantor = savedGuarantorRows.find(
          (row) =>
            Number(row?.['grntrKey']) ===
            group.controls.guarantorRefCdSeq.value,
        );

        const guarantorSequence = this.toNullableNumber(
          matchedGuarantor?.['crdtGrntrSeq'],
        );

        if (guarantorSequence !== null) {
          group.controls.crdtGrntrSeq.setValue(guarantorSequence, {
            emitEvent: false,
          });
        }
      });

      meta.removedGuarantors = [];
    });

    this.removedTermRows = [];

    this.config = {
      crdtTrmParamsLst: payload.crdtTrmParamsLst,
      rpStrtDt: payload.rpStrtDt,
      rpEndDt: payload.rpEndDt,
      rpymntFrqncy: payload.rpymntFrqncy,
      sprdRt: payload.sprdRt,
      crdtEnsrFee: payload.crdtEnsrFee,
      grcPrd: payload.grcPrd,
      noOfInstlmnt: payload.noOfInstlmnt,
      arrearFlg: payload.arrearFlg === 1 ? 1 : 0,
      upfrntFlg: payload.upfrntFlg === 1 ? 1 : 0,
      upFrntFee: payload.upFrntFee,
    };
  }

  private getProposalSequence(): number {
    return Number(
      this.crdtPrpslSeq ?? this.creditService.getCrdtPrpslSeq() ?? 0,
    );
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numeric = Number(value);

    return Number.isFinite(numeric) ? numeric : null;
  }

  private toDateString(value: unknown): string | null {
    if (typeof value !== 'string' || !value.trim()) {
      return null;
    }

    return value.slice(0, 10);
  }
}

function decimalNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as unknown;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    return /^\d+(\.\d{1,2})?$/.test(String(value).trim())
      ? null
      : { decimalNumber: true };
  };
}

function positiveDecimalValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as unknown;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numeric = Number(value);
    const isDecimal = /^\d+(\.\d+)?$/.test(String(value).trim());

    if (!isDecimal || !Number.isFinite(numeric)) {
      return { positiveDecimal: true };
    }

    return numeric > 0 ? null : { positiveDecimal: true };
  };
}

function dateRangeValidator(
  startControlName: string,
  endControlName: string,
  errorKey: string,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const startValue = control.get(startControlName)?.value as
      | string
      | null;
    const endValue = control.get(endControlName)?.value as
      | string
      | null;

    if (!startValue || !endValue) {
      return null;
    }

    const startDate = parseLocalDate(startValue);
    const endDate = parseLocalDate(endValue);

    if (!startDate || !endDate || startDate > endDate) {
      return { [errorKey]: true };
    }

    return null;
  };
}

function maxAmountValidator(
  valueControlName: string,
  limitControlName: string,
  errorKey: string,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.get(valueControlName)?.value;
    const limit = control.get(limitControlName)?.value;

    if (
      value === null ||
      value === undefined ||
      value === '' ||
      limit === null ||
      limit === undefined ||
      limit === ''
    ) {
      return null;
    }

    return Number(value) > Number(limit) ? { [errorKey]: true } : null;
  };
}

function maxProposedExposureValidator(getMaxTotal: () => number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numeric = Number(value);

    if (!Number.isFinite(numeric)) {
      return null;
    }

    const max = getMaxTotal();

    return numeric > max + 0.01 ? { maxProposedExposure: { max } } : null;
  };
}

function guarantorTotalValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const guarantorsArray = control.get('guarantors');

    if (!(guarantorsArray instanceof FormArray)) {
      return null;
    }

    const total = roundToTwoDecimals(
      guarantorsArray.controls.reduce((sum: number, guarantorGroup) => {
        const percentage = Number(
          guarantorGroup.get('percentage')?.value ?? 0,
        );

        return sum + (Number.isFinite(percentage) ? percentage : 0);
      }, 0),
    );

    return total > 100 ? { guarantorTotalExceeded: { total } } : null;
  };
}

function getValidationMessage(
  errors: ValidationErrors | null,
  labelKey: string,
  translate: TranslateService,
): string {
  if (!errors) {
    return '';
  }

  const label = translate.instant(labelKey);

  if (errors['required']) {
    return translate.instant('TERMS_PARAMS.VALIDATION.REQUIRED', { label });
  }

  if (errors['min']) {
    return translate.instant('TERMS_PARAMS.VALIDATION.MIN', {
      label,
      min: errors['min'].min,
    });
  }

  if (errors['max']) {
    return translate.instant('TERMS_PARAMS.VALIDATION.MAX', {
      label,
      max: errors['max'].max,
    });
  }

  if (errors['positiveDecimal']) {
    return translate.instant('TERMS_PARAMS.VALIDATION.POSITIVE', { label });
  }

  if (errors['decimalNumber']) {
    return translate.instant('TERMS_PARAMS.VALIDATION.NON_NEGATIVE', { label });
  }

  return translate.instant('TERMS_PARAMS.VALIDATION.INVALID', { label });
}

function toNumber(value: unknown, fallback: number): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function roundToTwoDecimals(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function sortOptions(options: DropdownOption[]): DropdownOption[] {
  return [...options].sort(
    (first, second) =>
      Number(first.original?.sortOrder ?? 0) -
      Number(second.original?.sortOrder ?? 0),
  );
}

function parseLocalDate(value: string): Date | null {
  const parts = value.slice(0, 10).split('-').map(Number);

  if (
    parts.length !== 3 ||
    parts.some((part) => !Number.isFinite(part))
  ) {
    return null;
  }

  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function extractSavedConfig(
  response: unknown,
): TermsParametersConfig | null {
  const source = response as Record<string, any> | null;

  const candidates = [source, source?.['data']];

  const match = candidates.find(
    (value) => value && Array.isArray(value['crdtTrmParamsLst']),
  );

  if (!match) {
    return null;
  }

  return {
    crdtTrmParamsLst: match['crdtTrmParamsLst'],
    rpStrtDt: match['rpStrtDt'] ?? null,
    rpEndDt: match['rpEndDt'] ?? null,
    rpymntFrqncy: match['rpymntFrqncy'] ?? null,
    sprdRt: match['sprdRt'] ?? null,
    crdtEnsrFee: match['crdtEnsrFee'] ?? null,
    grcPrd: match['grcPrd'] ?? null,
    noOfInstlmnt: match['noOfInstlmnt'] ?? null,
    prpsdExpsr: match['prpsdExpsr'] ?? match['prpsdExposure'] ?? null,
    arrearFlg: Number(match['arrearFlg']) === 1 ? 1 : 0,
    upfrntFlg: Number(match['upfrntFlg']) === 1 ? 1 : 0,
    upFrntFee: match['upFrntFee'] ?? null,
  };
}

function extractErrorMessage(error: unknown, fallback: string): string {
  const source = error as Record<string, any> | null;

  return (
    source?.['error']?.['message'] ??
    source?.['error']?.['errorMessage'] ??
    source?.['message'] ??
    fallback
  );
}
